import { useRequestedDocumentsListQuery, usePublicUploadDocumentsMutation } from './hooks';
import { CardBody } from 'components/card/card-body';
import { FileInput, defaultFormats } from 'components/file/file-input';
import { Loading } from 'components/loading';
import { InlineSvg } from 'components/svg';
import { RequestDocumentModel } from 'features/employees/api/models/employee';
import _ from 'lodash';
import { FC, useMemo, useState } from 'react';
import { Card } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { toAbsoluteUrl } from 'utils/assets';
import { insertItem, updateItem } from 'utils/collections';
import { getBase64, getExtension } from 'utils/files';

export const PublicUploadDocumentsForm: FC = () => {
  const [submittedDocuments, setRequestDocuments] = useState<RequestDocumentModel[]>([]);
  const { userUid } = useParams();
  const { data: requestedDocuments, isLoading } = useRequestedDocumentsListQuery({
    uid: userUid ?? ''
  });

  const requiredDocs = useMemo(
    () => requestedDocuments?.categories?.filter((cat) => cat.existsAwaitRequest),
    [requestedDocuments]
  );

  const { mutate: uploadRequestedDocuments, isLoading: isCreatingDocumentRequest } = usePublicUploadDocumentsMutation();

  const handleUpdateDocuments = async (id: number, documentType: string, file: File) => {
    const personalFileBase64: string = (await getBase64(file)) as string;
    const index = submittedDocuments.findIndex((item) => item.requestDocumentId == id);
    if (index != -1) {
      const updatedFile = {
        ...submittedDocuments[index],
        personalFileBase64
      };
      setRequestDocuments(updateItem(submittedDocuments, updatedFile, index));
    } else {
      setRequestDocuments(
        insertItem(submittedDocuments, { personalFileBase64, personalFileName: file.name, requestDocumentId: id }, 0)
      );
    }
  };

  const handleUploadDocuments = () => {
    uploadRequestedDocuments({
      input: {
        uid: userUid ?? '',
        requestBody: submittedDocuments
      }
    });
  };

  const getSelectedFile = (id: number) => {
    const submittedFileName = submittedDocuments.find((item) => item.requestDocumentId == id);
    return submittedFileName ? submittedFileName.personalFileName : '';
  };

  return (
    <Card>
      <CardBody className="py-4">
        <div className="row">
          <div className="col">
            <div className="fs-2 fw-bold">
              <span>Upload Required documents</span>
              <br />
            </div>
            {requiredDocs?.map((item) => (
              <div key={item.id}>
                <p className="fs-6 mt-7">
                  Please upload you <span className="fw-bold">{item.name}</span>
                </p>
                <div>
                  <FileInput
                    label="Upload new"
                    supportFormats={defaultFormats}
                    onChange={(file: File) => handleUpdateDocuments(item.id!, item.name!, file)}
                  />
                  <span className="ms-5">pdf, jpeg or png</span>
                  <div className="d-flex flex-column mt-10">
                    {getSelectedFile(item.id!) && (
                      <>
                        <label className="form-label">Selected file:</label>
                        <div className="d-flex flex-row">
                          <InlineSvg
                            path={toAbsoluteUrl(`/media/svg/files/${getExtension(getSelectedFile(item.id!))}.svg`)}
                            className="svg-icon-4x"
                          />
                          <div className="d-flex flex-column align-self-center">
                            <span className="fw-bold text-truncate mw-900px">{getSelectedFile(item.id!)}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="separator separator-dashed my-7"></div>
              </div>
            ))}
          </div>
          {requiredDocs?.length === 0 && <div>No documents required</div>}
          <div className="row mt-12">
            <div className="col">
              <button
                disabled={_.isEmpty(submittedDocuments) || isLoading || isCreatingDocumentRequest}
                onClick={handleUploadDocuments}
                className="btn btn-primary"
                type="button"
              >
                Save
              </button>
            </div>
          </div>
        </div>
        {isLoading && <Loading />}
      </CardBody>

      <ToastContainer autoClose={2000} theme="light" />
    </Card>
  );
};
